import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail, formatAxiosError, consumeAuthBlock } from "@/api";
import { BrandLogo, BrandWordmark, BRAND } from "@/components/Brand";
import { MessageCircle } from "lucide-react";

// WhatsApp do suporte/master (formato internacional sem +)
const SUPPORT_WHATSAPP = "5551998109841";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const [expiredInfo, setExpiredInfo] = useState(null); // { message, whatsapp }

  React.useEffect(() => {
    if (user) nav("/", { replace: true });
  }, [user, nav]);

  // Se o usuário foi derrubado por desativação/assinatura expirada durante
  // uma sessão ativa, o api.js armazena a mensagem; exibimos ao montar.
  React.useEffect(() => {
    const blk = consumeAuthBlock();
    if (blk && blk.message) {
      setExpiredInfo({ kind: blk.kind, message: blk.message, whatsapp: blk.whatsapp || "" });
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setExpiredInfo(null);
    try {
      await login(email, password);
      toast.success("Bem-vindo!");
      nav("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      // Mensagens específicas vindas do backend: trial_expired (402),
      // subscription_expired (402), account_disabled (403). Todas trazem
      // `message` (texto a exibir) e `support_whatsapp` (link CTA).
      if ((status === 402 || status === 403) && detail && typeof detail === "object"
          && ["trial_expired", "subscription_expired", "account_disabled"].includes(detail.error)) {
        setExpiredInfo({
          kind: detail.error,
          message: detail.message || "",
          whatsapp: detail.support_whatsapp || "",
          pay_url: detail.pay_url || "",
        });
      } else if (err.response?.status === 401) {
        toast.error("Email ou senha incorretos.");
      } else {
        toast.error(formatAxiosError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden bg-[#020617]">
        {/* Background decorative gradients */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl"/>
          <div className="absolute -bottom-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-purple-600/20 blur-3xl"/>
          <div className="absolute top-1/3 left-1/4 w-72 h-72 rounded-full bg-indigo-500/10 blur-3xl"/>
        </div>

        <div className="relative z-10 flex items-center gap-3 text-white">
          <span className="text-xs uppercase tracking-[0.3em] text-white/40">Acesso seguro</span>
        </div>

        <div className="relative z-10 flex flex-col items-center text-white">
          <BrandLogo size={180} />
          <BrandWordmark className="mt-4"/>
          <p className="mt-10 max-w-md text-center text-white/60 text-sm leading-relaxed">
            Plataforma completa de gestão para sua loja: estoque, pedidos, vendas, catálogo online, assistência técnica e financeiro — tudo em um só lugar.
          </p>
        </div>

        <div className="relative z-10 text-xs text-white/30">
          © {new Date().getFullYear()} {BRAND.name} · {BRAND.tagline}
        </div>
      </div>

      {/* Login form */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="lg:hidden flex flex-col items-center mb-2">
            <img src={BRAND.logo} alt="" className="w-20 h-20 object-contain"/>
            <div className="font-display font-bold text-xl mt-2">
              <span>Gestão </span>
              <span style={{ background: "linear-gradient(90deg, #4F46E5, #A855F7)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Expert</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400 mt-1">{BRAND.tagline}</div>
          </div>
          <div>
            <p className="label-tiny mb-2">Acesso</p>
            <h2 className="font-display text-3xl tracking-tight">Entrar na sua loja</h2>
            <p className="text-sm text-neutral-500 mt-2">Use seu email e senha de administrador.</p>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <Input id="password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="login-password-input" />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-[#0A0A0A] hover:bg-black text-white" data-testid="login-submit-button">
            {busy ? "Entrando…" : "Entrar"}
          </Button>
          {expiredInfo && (
            <div className="border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-900/40 rounded-md p-4 text-sm text-red-800 dark:text-red-300 space-y-2" data-testid="expired-message">
              <div className="whitespace-pre-line font-medium" data-testid={`expired-kind-${expiredInfo.kind || "generic"}`}>
                {expiredInfo.message}
              </div>
              {expiredInfo.pay_url && (
                <Button
                  type="button" size="sm"
                  onClick={()=>{ window.location.href = expiredInfo.pay_url; }}
                  className="w-full bg-[#635BFF] hover:bg-[#5749f0] text-white"
                  data-testid="pay-now-button"
                >
                  {expiredInfo.kind === "trial_expired" ? "Assinar o plano" : "Pagar agora"}
                </Button>
              )}
              {expiredInfo.whatsapp && (
                <Button
                  type="button"
                  size="sm"
                  onClick={()=>{
                    const phone = (expiredInfo.whatsapp || "").replace(/\D/g, "");
                    const full = phone.startsWith("55") ? phone : "55" + phone;
                    // Mensagem do WhatsApp adapta conforme o tipo de bloqueio
                    const msgByKind = {
                      trial_expired: "Olá! Meu período de teste do Gestão Expert encerrou e quero contratar.",
                      subscription_expired: "Olá! Minha assinatura do Gestão Expert venceu, gostaria de renovar.",
                      account_disabled: "Olá! Minha conta do Gestão Expert foi desativada, gostaria de regularizar.",
                    };
                    const txt = msgByKind[expiredInfo.kind] || "Olá! Preciso de ajuda com minha conta do Gestão Expert.";
                    window.open(`https://wa.me/${full}?text=${encodeURIComponent(txt)}`, "_blank");
                  }}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="renew-whatsapp-button"
                >
                  <MessageCircle size={14} className="mr-1.5"/> Falar no WhatsApp
                </Button>
              )}
            </div>
          )}
          <div className="text-xs text-neutral-500 text-center">
            <button
              type="button"
              onClick={() => {
                const msg = encodeURIComponent(
                  `Olá! Preciso de ajuda para redefinir minha senha do ${BRAND.name}.\n\nMeu e-mail de cadastro: ${email || "[informar e-mail]"}`
                );
                window.open(`https://wa.me/${SUPPORT_WHATSAPP}?text=${msg}`, "_blank");
              }}
              className="inline-flex items-center gap-1 underline hover:text-[#0A0A0A] transition"
              data-testid="forgot-password-link"
            >
              <MessageCircle size={12}/> Esqueci minha senha — falar com suporte
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
